import { useSelector } from 'react-redux';
import Global from '../../../../../../../store/global-selectors';
import { pages } from '../../../../../../../pages/settings/nav-items';
import { usePaymentAndSubscriptionPageContext } from '../../../../context/payment-and-subscription-page-context';
import { useLocale } from '../../../../../../../intl/hooks';

export const SS_BRIGHTBACK_DOMAIN_NAME = '://cancel.shipstation.com';
export const BRIGHTBACK_CANCELLATION_QUERY_STRING = '?cancelled=true';

// FIELD NAMES ARE DEFINED IN THE BRIGHTBACK SETTINGS PAGE
const BRIGHTBACK_FIELD_MAPPINGS = {
  SS_APP_ID: 'app_id',
  SELLER_ID: 'internal_id',
  COMPANY_NAME: 'company_name',
  EMAIL: 'email',
  SAVED_RETURN_URL: 'save_return_url',
  CANCELLED_RETURN_URL: 'cancel_confirmation_url',
  account_COMPANY_NAME: 'company_name',
  account_CREATED_AT: 'created_at',
  account_PLAN: 'plan',
  custom_PLAN_CODE: 'plan_code',
  custom_PLAN_NAME: 'plan_name',
  custom_PLAN_PROFILE_CODE: 'plan_profile_code',
  custom_SELLER_ID: 'seller_id',
  custom_USER_ID: 'user_id',
  custom_USER_FULL_NAME: 'user_full_name',
  custom_USERNAME: 'username',
  custom_ACCOUNT_MANAGER_NAME: 'account_manager_name',
  custom_ACCOUNT_MANAGER_ID: 'account_manager_id',
  custom_BILLING_PLAN_ID: 'billing_plan_id',
  custom_BILLING_MONTHLY_PRICE_USD: 'monthly_price_usd',
  custom_HOME_COUNTRY: 'seller_home_country',
  custom_BILLING_ADDRESS_STATE: 'billing_address_state',
  custom_BILLING_PROVIDER_ID: 'billing_provider_id',
  custom_USER_LANGUAGE: 'user_language_abbrev',
  custom_SHIPMENT_VOLUME_FOR_CURRENT_BILLING_CYCLE: 'shipment_volume',
  custom_CHANNEL_COUNT: 'channels', // # of unique active marketplaces (not stores). Marketing/Business refers to "marketplaces" as (selling) "channels"
  custom_SHIPMENT_ALLOTMENT: 'shipment_allotment',
  custom_SHIPMENT_ALLOTMENT_IS_UNLIMITED: 'shipment_allotment_is_unlimited',
  custom_SHIPMENT_ALLOTMENT_IS_CUSTOM: 'shipment_allotment_is_custom',
  custom_SHIPMENT_ALLOTMENT_LIMIT_CODE: 'shipment_allotment_limit_code',
};

// SS VALUES THAT WILL MAP TO SPECIFIC FIELDS IN THE BRIGHTBACK SETTINGS PAGE
const ssBrightbackAppId = window.SS_GLOBALS.frontEndConfig.brightbackAppId;
const subscriptionPageUrl = `${window.location.origin}/${pages.settings.location}/${pages.paymentAndSubscription.location}`;
const savedReturnUrl = subscriptionPageUrl;
export const cancelledReturnUrl = `${subscriptionPageUrl}${BRIGHTBACK_CANCELLATION_QUERY_STRING}`;
const stampsAccountCancellationUrl = `${window.location.origin}/${pages.settings.location}/${pages.paymentAndSubscription.location}/${pages.cancelStamps.location}`;

const getCancelledReturnUrl = (sellerStampsProviders) => {
  if (sellerStampsProviders?.length) {
    return stampsAccountCancellationUrl;
  }
  return cancelledReturnUrl;
};

export const useGetBrightbackData = () => {
  const { sellerStampsProviders, billingInfo } = usePaymentAndSubscriptionPageContext();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const shipmentUsage = useSelector(Global.selectSellerShipmentUsage);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { currentBillingPlan } = subscriptionData ?? {};
  const { billingPlanId, pricePerPeriod, shipmentLimit } = currentBillingPlan ?? {};
  const sellerId = useSelector(Global.selectSellerSellerId);
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const companyName = useSelector(Global.selectSellerCompanyName);
  const accountManager = useSelector(Global.selectSellerAccountManager);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const { locale: userLocale } = useLocale();
  const mappedData = {
    [BRIGHTBACK_FIELD_MAPPINGS.SS_APP_ID]: ssBrightbackAppId,
    [BRIGHTBACK_FIELD_MAPPINGS.EMAIL]: currentUser.email,
    [BRIGHTBACK_FIELD_MAPPINGS.SAVED_RETURN_URL]: savedReturnUrl,
    [BRIGHTBACK_FIELD_MAPPINGS.CANCELLED_RETURN_URL]: getCancelledReturnUrl(sellerStampsProviders),
    account: {
      [BRIGHTBACK_FIELD_MAPPINGS.SELLER_ID]: sellerId,
      [BRIGHTBACK_FIELD_MAPPINGS.account_COMPANY_NAME]: companyName,
      [BRIGHTBACK_FIELD_MAPPINGS.account_CREATED_AT]: planInfo?.createDateTimeUTC,
      [BRIGHTBACK_FIELD_MAPPINGS.account_PLAN]: planInfo?.plan?.name,
    },
    custom: {
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SELLER_ID]: sellerId,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_USER_ID]: currentUser.userId,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_USER_FULL_NAME]: currentUser.name,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_USERNAME]: currentUser.username,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_PLAN_CODE]: planInfo?.plan?.code,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_PLAN_NAME]: planInfo?.plan?.name,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_PLAN_PROFILE_CODE]: planInfo?.planProfileCode,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_ACCOUNT_MANAGER_NAME]: accountManager?.name,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_ACCOUNT_MANAGER_ID]: accountManager?.accountManagerId,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_BILLING_PLAN_ID]: billingPlanId,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_BILLING_MONTHLY_PRICE_USD]: pricePerPeriod?.value,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_HOME_COUNTRY]: sellerHomeCountry,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_BILLING_PROVIDER_ID]: planInfo?.billingProviderID,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_USER_LANGUAGE]: userLocale,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SHIPMENT_VOLUME_FOR_CURRENT_BILLING_CYCLE]: shipmentUsage?.totalUsage ?? 0,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_CHANNEL_COUNT]: planInfo?.usage?.channels ?? 0,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_BILLING_ADDRESS_STATE]: billingInfo?.address?.state ?? '',
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SHIPMENT_ALLOTMENT]: shipmentLimit?.limit ?? 0,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SHIPMENT_ALLOTMENT_IS_UNLIMITED]: shipmentLimit?.isUnlimited ?? false,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SHIPMENT_ALLOTMENT_IS_CUSTOM]: shipmentLimit?.isCustom ?? false,
      [BRIGHTBACK_FIELD_MAPPINGS.custom_SHIPMENT_ALLOTMENT_LIMIT_CODE]: shipmentLimit?.limitCode ?? '',
    },
  };

  return mappedData;
};
